"use client";
import { ReservationConfirmContent } from "@/components/medical-checkups/ReservationConfirmDialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { pagesPath } from "@/utils/pages-path";
import ExternalLinkIcon from "@public/icons/external-link-icon.svg";
import MykenpoIcon from "@public/icons/mykenpo-icon.svg";
import Link from "next/link";
import { useState } from "react";
import { MdLanguage, MdLocalHospital, MdPhone } from "react-icons/md";

type ReservationGuideDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reservationUrl: string | null;
  url: string | null;
  reservationTel: string | null;
  reservationFormUrl: string;
};

export const ReservationGuideDialog = ({
  open,
  onOpenChange,
  reservationUrl,
  url,
  reservationTel,
  reservationFormUrl,
}: ReservationGuideDialogProps) => {
  const [dialogStep, setDialogStep] = useState<"guide" | "confirm">("guide");
  const reservationLink = reservationUrl || url;

  const handleOpenChange = (value: boolean) => {
    if (!value) {
      setDialogStep("guide");
    }
    onOpenChange(value);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="mx-auto flex max-h-[80vh] max-w-[calc(100vw-3rem)] flex-col gap-0 p-0 md:max-w-xl"
        aria-describedby={undefined}
      >
        {/* 案内ステップ */}
        {dialogStep === "guide" && (
          <>
            <div className="shrink-0 border-b border-monotone-2 px-6 pt-6 pb-4 md:px-8 md:pt-8">
              <DialogTitle className="text-lg font-bold md:text-xl">
                予約案内
              </DialogTitle>
            </div>

            <div className="space-y-6 overflow-y-auto px-6 pt-6 pb-6 md:px-8 md:pb-8">
              {/* STEPカード */}
              <div className="flex items-stretch gap-2 md:gap-3">
                <div className="flex flex-1 flex-col items-center rounded-lg border border-monotone-3 bg-monotone-0.5 px-3 py-4">
                  <div className="flex items-baseline gap-1 text-primary-6 md:flex-col md:items-center md:gap-0">
                    <span className="text-xs">STEP</span>
                    <span className="text-2xl font-bold">1</span>
                  </div>
                  <div className="mt-2 w-full border-t border-monotone-3 pt-3">
                    <div className="flex flex-col items-center gap-1 text-xs md:flex-row md:justify-center">
                      <MdLocalHospital className="h-4 w-4 text-primary-6" />
                      <span className="text-center">
                        健診機関にて
                        <br className="md:hidden" />
                        予約を実施
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex shrink-0 items-center">
                  <span className="text-monotone-5">→</span>
                </div>
                <div className="flex flex-1 flex-col items-center rounded-lg border border-monotone-3 bg-monotone-0.5 px-3 py-4">
                  <div className="flex items-baseline gap-1 text-primary-6 md:flex-col md:items-center md:gap-0">
                    <span className="text-xs">STEP</span>
                    <span className="text-2xl font-bold">2</span>
                  </div>
                  <div className="mt-2 w-full border-t border-monotone-3 pt-3">
                    <div className="flex flex-col items-center gap-1 text-xs md:flex-row md:justify-center">
                      <MykenpoIcon className="h-4 w-4 shrink-0" />
                      <span className="text-center">
                        予約完了報告と
                        <br className="md:hidden" />
                        問診票の回答
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-b border-monotone-2 pb-6">
                <p className="text-sm">
                  健診機関へ予約を行った後は、「予約完了の報告」および「問診への回答」にご協力ください。予約完了の報告は、当組合や事業所が予約内容を把握するために、必要となります。
                </p>
              </div>

              {/* STEP 1: 健診機関にて予約を実施 */}
              <div className="space-y-3 pt-2">
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-bold text-primary-6">1</span>
                  <span className="text-primary-6">|</span>
                  <div className="flex items-center gap-1">
                    <MdLocalHospital className="h-5 w-5 text-primary-6" />
                    <span className="text-base font-bold">
                      健診機関にて「予約を実施」
                    </span>
                  </div>
                </div>
                <p className="text-sm">
                  予約は健診機関へ直接お願いします。その際、
                  <Link
                    href={pagesPath.members.insurance_card.$url().path}
                    className="text-primary-6 underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    保険証の記号・番号・枝番
                  </Link>
                  をご用意の上、VCスタートアップ健康保険組合の健保補助が適用される検査を希望する旨を必ずお伝えください。
                </p>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <Button variant="primary" className="h-12 md:h-14" asChild>
                    <a
                      href={reservationLink ?? undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-disabled={!reservationLink}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <MdLanguage className="h-5 w-5 md:h-6 md:w-6" />
                        <span className="text-sm md:text-base">
                          {reservationLink === reservationUrl
                            ? "健診機関のWeb予約"
                            : "Webサイトで確認"}
                        </span>
                        <ExternalLinkIcon className="h-4 w-4 shrink-0 fill-white" />
                      </div>
                    </a>
                  </Button>

                  <Button variant="primary" className="h-12 md:h-14" asChild>
                    <a
                      href={
                        reservationTel ? `tel:${reservationTel}` : undefined
                      }
                      aria-disabled={!reservationTel}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <MdPhone className="h-5 w-5 md:h-6 md:w-6" />
                        <span className="text-sm md:text-base">
                          {reservationTel
                            ? `電話予約 ${reservationTel}`
                            : "電話予約"}
                        </span>
                      </div>
                    </a>
                  </Button>
                </div>
                {reservationLink && (
                  <p className="text-xs text-monotone-5">
                    ※外部サイトへアクセスします
                  </p>
                )}
              </div>

              {/* STEP 2: mykenpoにて予約完了の報告と問診票の回答 */}
              <div className="space-y-3 border-t border-monotone-2 pt-6">
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-bold text-primary-6">2</span>
                  <span className="text-primary-6">|</span>
                  <div className="flex items-center gap-1">
                    <MykenpoIcon className="h-5 w-5 shrink-0" />
                    <span className="text-base font-bold">
                      mykenpoにて「予約完了の報告」と「問診票の回答」
                    </span>
                  </div>
                </div>
                <p className="text-sm">
                  健診機関の予約後、予約完了の報告および問診票へのご回答にご協力ください。
                </p>
                <Button
                  variant="primary"
                  className="h-12 w-full md:h-14"
                  onClick={() => setDialogStep("confirm")}
                >
                  予約完了の報告
                </Button>
              </div>
            </div>
          </>
        )}

        {/* 確認ステップ */}
        {dialogStep === "confirm" && (
          <ReservationConfirmContent
            title="健診機関にて予約は完了していますか？"
            description="予約完了の報告は健診機関にて予約後にお願いいたします。"
            reservationFormUrl={reservationFormUrl}
            confirmLabel="予約完了の報告"
            confirmLabelWide="予約完了の報告をする"
            onBack={() => setDialogStep("guide")}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
